import { PROGRAMS_MEQ } from 'components/common/vendorConfig';
import { useAuthContext } from 'contexts/AuthContext';
import type { AuthContextType } from 'contexts/AuthContext';
import FHIRFetcher from 'data/FHIRFetcher';
import FoodUpdater from 'data/FoodUpdater';
import type { Bundle, BundleEntry, Identifier } from 'fhir/r4';
import moment from 'moment';
import { useEffect, useState } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Button, Form, Modal, Select, Comment, TextArea, Header, Divider, Message } from 'semantic-ui-react';
import type { InputOnChangeData } from 'semantic-ui-react';
import useSWR from 'swr';
import type { Fetched, FoodOrder, OrderNote, ProgramBudget, ReferralSummary } from 'types';

import * as Helpers from '../../../../utils/Helpers';
import { calculateMealPricing } from '../utils/foodUtils';

/**
 * What the edit-order modal accepts.
 *
 * Established from its single JSX call site in `./index.tsx`, which renders it
 * only when exactly one order is selected.
 *
 * That call site is guarded by `selectedOrdersLength === 1 && selectedOrder &&`,
 * so `selectedOrder` is present whenever this component exists at all; the props
 * required below are the ones that guard covers plus those the parent supplies
 * from its own `useState` setters, its `useSWR` `mutate`, and a module-local
 * literal (`invoiceStatusList`) — none of which can be absent. The remainder stay
 * optional because they are forwarded from the parent's OWN optional props and so
 * really can arrive `undefined`.
 *
 * Two props are accepted but never read, and are declared only so the call site
 * keeps compiling: `ordersMealEquivalents` / `setOrdersMealEquivalents` (passed
 * at lines 1228-1229) and `allReferralData` (line 1236). The meal-equivalent
 * pair is live in the sibling `AddOrder`, so its presence here reads as symmetry
 * with that call rather than as an intended use.
 *
 * SUSPECTED DEFECT — see the note in the `ordersData` effect for the
 * `programOrderCost` / `programOrdersCost` name mismatch and the budget guard it
 * disables.
 */
type UpdateOrderProps = {
  /** Accepted but unread. */
  allReferralData?: ReferralSummary[];
  /**
   * The parent's own `useAuthContext()` result, passed down. Three `FoodUpdater`
   * calls read `.token` off it while the rest of the file reads the identical
   * value from this component's own `useAuthContext()` — redundant, not wrong.
   */
  authInfo: AuthContextType;
  /** Status values that mark an order as invoiceable; membership gates a branch. */
  invoiceStatusList: string[];
  /** Modal visibility, mirrored into local state. */
  open?: boolean;
  /** Every order in the current dataset, scanned to build the PK triage dropdown. */
  ordersData?: FoodOrder[];
  /** Accepted but unread. */
  ordersMealEquivalents?: number;
  /** Revalidates the parent's orders query after a successful update. */
  ordersMutate: () => void;
  /** The payer org; membership in `PROGRAMS_MEQ` reveals the budget fields. */
  org?: string;
  /**
   * Read but never passed — see the SUSPECTED DEFECT note below. The call site
   * passes the plural `programOrdersCost` instead.
   */
  programOrderCost?: number;
  /** The program's spend ceiling, compared against in the (unreachable) guard. */
  programMAXBudget?: number;
  programMAXMealEquivalents?: number;
  programOrdersCost?: number;
  programBudget?: ProgramBudget[];
  /**
   * The order being edited. Its `orderNotes` and status drive the whole form,
   * and `updateOrderFunc` writes the edited fields back onto this same object
   * before posting it — which is why the write targets are named on `FoodOrder`.
   */
  selectedOrder: FoodOrder;
  setLoading: (loading: boolean) => void;
  setOpen: (open: boolean) => void;
  /** Accepted but unread. */
  setOrdersMealEquivalents?: (mealEquivalents: number) => void;
  setProgramMAXBudget?: (budget: number) => void;
  setProgramOrdersCost?: (cost: number) => void;
  setWorking: (working: boolean) => void;
  /** Labels the parent's progress message ('Updating'). */
  setWorkingOn: (workingOn: string) => void;
  /** A vendor code; 'SB' adds two extra statuses to the dropdown. */
  vendor?: string;
};

/**
 * A write-through view of an order for the computed-key updates in this file.
 *
 * The form writes `order[attributeName]` from the changed control, and
 * `updateOrderFunc` `delete`s the `PATIENT_ATTRIBUTES` keys that
 * `orders/export.tsx` had written onto the same object. Neither is expressible
 * against {@link FoodOrder} alone.
 *
 * Used only at the write sites, so reads elsewhere keep named-field checking.
 */
type OrderWriteView = FoodOrder & Record<string, unknown>;

function UpdateOrder(props: UpdateOrderProps) {
  const PATIENT_ATTRIBUTES = [
    'firstName',
    'lastName',
    'delivery_addressLine1',
    'delivery_addressLine2',
    'delivery_city',
    'delivery_state',
    'delivery_zip',
    'email',
    'homePhone',
    'mobilePhone',
  ];

  const statusList = [
    'new',
    'SENT',
    'DELIVERED',
    'CANCELLED',
    'exported',
    'SHIPPED',
    'RESHIPPED',
    'PARTIALLY-FULFILLED',
    'UNFULFILLED',
    'EBT PENDING',
  ];

  const triageReasonList = [
    'DAMAGED OR SPOILED',
    'NEVER ARRIVED',
    'STOLEN',
    'MISSING ITEMS',
    'NO FAULT TO VENDOR - WRONG ADDRESS',
    'NO FAULT TO VENDOR - MEMBER DISSATISFACTION',
    'NO FAULT TO VENDOR - NOURISHEDRX ERROR',
  ];

  if (props.vendor === 'SB') {
    statusList.push('ACCEPTED');
    statusList.push('UNACCEPTED');
  }
  const authInfo = useAuthContext();
  const noteList = props.selectedOrder && props.selectedOrder.orderNotes;
  const [state, setState] = useState<{ noteText?: string }>({ noteText: '' });
  const [updatedNote, setUpdatedNote] = useState<{ noteText?: string }>({ noteText: '' });
  /**
   * The pending edits, keyed by `FoodOrder` field name and merged onto the
   * selected order on submit. Written with computed keys (`orderDetails[
   * attributeName]`), so the key set is open at the type level even though every
   * key written is in fact a `FoodOrder` field.
   */
  let orderDetails: Record<string, unknown> = {};
  const [_buttonLoader, setButtonLoader] = useState(false);
  const [editNotePageOpen, setEditNotePageOpen] = useState(false);
  // The index of the note row being edited, or `null` when the editor is closed.
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(null);
  const [_ordersMessage, setOrdersMessage] = useState('');
  const [_editOrderPageOpen, setEditOrderPageOpen] = useState(false);
  const [_hideMessage, setHideMessage] = useState(false);
  const { noteText } = state;
  const [errorMsg, setErrorMsg] = useState('');
  const [referralIdList, setReferralIdList] = useState<string[]>([]);
  const [triageOrderList, setTriageOrderList] = useState<string[]>([]);
  const [tempProgramOrderCost, setTempProgramOrderCost] = useState(0);
  const [tempOrder, _setTempOrder] = useState({ ...props.selectedOrder });
  //let [overrideFoodPrice, setOverrideFoodPrice] = useState(false);
  const [budgetOverrideChecked, setBudgetOverrideChecked] = useState(false);

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Fetched<Bundle>) => {
      if (data && data.entry && data.entry.length > 0) {
        const tempReferralIdList: string[] = [];
        data?.entry?.forEach((observation: BundleEntry) => {
          const referralId = (observation?.resource as { identifier?: Identifier[] } | undefined)?.identifier?.find(
            (obj: Identifier) => obj.system === 'https://projectwell.io/fhir/identifiers/referral-id'
          )?.value;
          if (referralId) {
            tempReferralIdList.push(referralId);
          }
        });
        setReferralIdList(tempReferralIdList);
      }
    },
  };

  //fetch referral data
   
  // SWR types `error` as `any` unless its second generic is given, and supplying one
  // here pins the data generic against FHIRFetcher's own. The binding is unread, but
  // removing it is a real read fewer.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars
  const { data: referralData, error: referralError } = useSWR(
    [
      `/Observation/?patient=${props?.selectedOrder?.patientId}&code=REFERRAL-TYPE&identifier:not=https://projectwell.io/fhir/identifiers/referral-status|ENTERED-IN-ERROR`,
      authInfo.token,
    ],
    FHIRFetcher,
    options
  );

  const addNoteField = (e: React.ChangeEvent<HTMLInputElement>, { name, value }: InputOnChangeData) => {
    setState({ [name]: value });
  };

  const handleNoteChange = (e: React.SyntheticEvent<HTMLElement>, { name: _name, value }: { name?: string; value: string }) => {
    setUpdatedNote({ noteText: value });
  };

  useEffect(() => {
    if (props?.ordersData) {
      const tempOrdersId: string[] = [];
      props?.ordersData?.forEach((order) => {
        const orderCreatedDate = moment(order?.createdDate).utc().format('YYYY-MM-DD');
        //only show the recent 2 months orders for triage order dropdown for PK orders.
        if (
          order.status !== 'draft' &&
          moment().diff(orderCreatedDate, 'months') < 2 &&
          order.vendorId === 'PK' &&
          order.orderId !== props?.selectedOrder.orderId
        ) {
          tempOrdersId.push(order.orderId!);
        }
      });
      setTriageOrderList(tempOrdersId);
      // SUSPECTED DEFECT — REPORTED, NOT FIXED. `programOrderCost` (singular) is
      // not a prop anyone passes: the sole call site, `./index.tsx:1140`, passes
      // `programOrdersCost` (plural). So this always takes the `?? 0` branch,
      // `tempProgramOrderCost` stays `0` for the component's whole life, and the
      // over-budget guard at line 323 — `tempProgramOrderCost >
      // props.programMAXBudget` — can never be true for a non-negative budget.
      // The "Total order cost exceeds the program budget limit" block is
      // therefore unreachable on the update path. Declared below as
      // accepted-but-unpassed so the read stays type-checked and visible.
      // Renaming it to the plural would switch that guard on, which is a
      // behaviour change with a clinical/financial consequence, so it is not
      // done here.
      setTempProgramOrderCost(props?.programOrderCost ?? 0);
    }
  }, [props?.ordersData]);

  useEffect(() => {
    setEditOrderPageOpen(props.open!);
  }, [props.open]);

  // The below block may be needed in the future ti handle program budget for update manual orders.
  // useEffect(() => {
  // let referralData = props.allReferralData?.filter((item) => item.status.toUpperCase() ===
  // 'IN-PROGRESS');
  //   // setReferralId(referralData?.length > 0 ? referralData[0]?.referralId : '');

  // let budget = getProgramBudget(referralData?.length > 0 ? referralData[0]?.referralId : '',
  // props.programBudget);

  //   props.setProgramOrdersCost(budget.programOrderCost);
  //   props.setProgramMAXBudget(budget.programFoodBudget);
  // }, [props.allReferralData, props.programBudget]);

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Deleting a note throws on an order that
   * has never had one.
   *
   * `FoodOrder.orderNotes` is optional and orders are created without it — neither
   * `createEBTPendingOrder` in `./index.tsx` nor `AddOrder` sets the field, so a
   * freshly created order carries no `orderNotes` at all. `.splice` on `undefined`
   * is a TypeError, which is unhandled here: it escapes before `setButtonLoader`,
   * so the modal's spinner never starts and the click reads as inert.
   *
   * The button that reaches this is rendered per existing note, so today it is only
   * clickable when `orderNotes` is a non-empty array. It fires the moment anything
   * calls `removeNote` outside that map. Adding `?.` would silently skip the delete
   * and still post the order, which is the wrong repair — the caller believes it is
   * removing a note.
   */
  const removeNote = (noteIndex: number) => {
    const newOrder = props.selectedOrder;
    newOrder.orderNotes!.splice(noteIndex, 1); // remove selected note
    setButtonLoader(true);
    //update order with removed note
    newOrder['lastUpdated'] = moment().utc().format();
    newOrder['lastUpdatedBy'] = authInfo.practitionerName;

    // REMOVE PII here from the order before it gets updated
    PATIENT_ATTRIBUTES.forEach((attribute) => {
      delete (newOrder as OrderWriteView)[attribute];
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FoodUpdater(`/order/` + newOrder.orderId, props.authInfo.token, newOrder).then((response) => {
      if (response.statusCode === '200') {
        setOrdersMessage('Note removed Successfully!');
      } else {
        setOrdersMessage('Removing Error - ' + response.message);
      }
      setButtonLoader(false);
    });
  };

  const editNote = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setButtonLoader(true);

    const newOrder = props.selectedOrder;
    let notepayload: OrderNote[] = [];
    const currentPayload: OrderNote = {
      author: authInfo.practitionerName,
      time: moment().utc().format(),
      text: updatedNote.noteText!,
    };
    if (noteList) {
      notepayload = noteList;
      notepayload.splice(selectedNoteIndex!, 1, currentPayload);
    }
    newOrder['orderNotes'] = notepayload;
    newOrder['lastUpdated'] = moment().utc().format();
    newOrder['lastUpdatedBy'] = authInfo.practitionerName;

    // REMOVE PII here from the order before it gets updated
    PATIENT_ATTRIBUTES.forEach((attribute) => {
      delete (newOrder as OrderWriteView)[attribute];
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FoodUpdater(`/order/` + newOrder.orderId, props.authInfo.token, newOrder).then((response) => {
      if (response.statusCode === '200') {
        setOrdersMessage('Note Edit Successfully!');
      } else {
        setOrdersMessage('Editing Error - ' + response.message);
      }
      setButtonLoader(false);
    });
    setEditNotePageOpen(false);
    setUpdatedNote({ noteText: '' });
    setSelectedNoteIndex(null);
  };

  const addNote = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setButtonLoader(true);

    const newOrder = props.selectedOrder;
    let notepayload: OrderNote[] = [];
    if (noteList) {
      notepayload = [...noteList];
    }
    const currentPayload: OrderNote = {
      author: authInfo.practitionerName,
      time: moment().utc().format(),
      text: state.noteText!,
    };
    notepayload.push(currentPayload);
    newOrder['orderNotes'] = notepayload;
    newOrder['lastUpdated'] = moment().utc().format();
    newOrder['lastUpdatedBy'] = authInfo.practitionerName;

    // REMOVE PII here from the order before it gets updated
    PATIENT_ATTRIBUTES.forEach((attribute) => {
      delete (newOrder as OrderWriteView)[attribute];
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FoodUpdater(`/order/` + newOrder.orderId, props.authInfo.token, newOrder).then((response) => {
      if (response.statusCode === '200') {
        setOrdersMessage('New Note Added Successfully!');
      } else {
        setOrdersMessage('Adding Error - ' + response.message);
      }
      setButtonLoader(false);
    });
    setState({ noteText: '' });
  };

  const handleDateChange = (e: React.SyntheticEvent<HTMLElement>, semanticprops: { value: unknown }) => {
    if (semanticprops.value === null) {
      return false;
    }
    orderDetails['deliveryDate'] = moment(semanticprops.value).format('YYYY-MM-DD');
  };

  const handleChange = (
    e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement; value?: unknown },
    attributeName: string,
    _previous?: unknown,
  ) => {
    const temp = props.selectedOrder;
    const oldStatus = props.selectedOrder['status'];
    const value = e.target && e.target.textContent;

    if (attributeName === 'status' && (value === 'NEW' || value === 'EXPORTED')) {
      temp[attributeName] = value.toLowerCase();
    } else if (attributeName === 'bundleQuantity') {
      temp[attributeName] = e.target && e.target.valueAsNumber && e.target.valueAsNumber;
      temp['quantity'] = temp[attributeName];
    } else if (attributeName === 'foodPricing' || attributeName === 'shippingFees') {
      const oldOrderPrice = calculateMealPricing(tempOrder, false, props);
      temp[attributeName] = e.target && e.target.value;
      const newOrderPrice = calculateMealPricing(temp, false, props);

      /**
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. Editing food price or shipping on a
       * member with no program budget sets the running order cost to `NaN`.
       *
       * `programOrdersCost` is forwarded from the parent's own optional prop and is
       * `undefined` for every caller that does not thread a program budget through —
       * the food-card and non-program order paths. `undefined` in this arithmetic
       * makes `Math.round` yield `NaN`, so `tempProgramOrderCost` becomes `NaN`, and
       * `NaN > props.programMAXBudget` is false: the over-budget guard on submit
       * silently passes for exactly the members whose budget could not be read.
       *
       * A `?? 0` here would make the guard pass for a different reason rather than
       * fix it, so the absence is left visible.
       */
      const tempSetPrice =
        (Math.round(props.programOrdersCost! - (oldOrderPrice ? oldOrderPrice : 0) + newOrderPrice) * 100) / 100; // Round to 2 decimal places
      setTempProgramOrderCost(tempSetPrice);
    } else if (attributeName === 'estimatedDeliveryDate') {
      temp[attributeName] = moment(e.value as moment.MomentInput).format('YYYY-MM-DD');
    } else if (attributeName === 'trackingCode' || attributeName === 'carrier') {
      if (!temp?.['deliveryInfo']) {
        temp['deliveryInfo'] = {};
      }
      (temp["deliveryInfo"] as Record<string, unknown>)[attributeName] = e.target && e.target.value;
    } else if (attributeName === 'mealEquivalents') {
      temp['mealEquivalents'] = e.target && parseInt(e.target.value);
    } else {
      (temp as OrderWriteView)[attributeName] = value;
    }
    if (temp['status'] && props.invoiceStatusList.includes(temp['status'].toLowerCase())) {
      temp['invoiceReady'] = '1';
    }
    if (temp['status'] && temp['status'].toUpperCase() === 'NEW' && oldStatus === 'EBT PENDING') {
      temp['orderDate'] = moment().toISOString();
    }
    orderDetails = temp;
  };

  const handleItemCheck = (_event: unknown, _data: unknown, _quantity: unknown) => {
    setBudgetOverrideChecked(!budgetOverrideChecked);
    //setOverrideFoodPrice(!overrideFoodPrice);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent 'e' and 'E' characters
    if (e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  function updateOrderFunc(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    setHideMessage(false);
    props.setWorkingOn('Updating');
    const selectedOrderId = props.selectedOrder.orderId;
    const temp = props.selectedOrder;
    const temp1 = orderDetails;
    if (Object.keys(temp1).length > 0) {
      for (const key of Object.keys(temp1)) {
        (temp as OrderWriteView)[key] = temp1[key];
      }
    }

    if (!temp.estimatedDeliveryDate || temp.estimatedDeliveryDate === 'Invalid date') {
      setErrorMsg('Please enter a valid estimate delivery date');
      props.setOpen(true);
    } else if (temp.foodPricing && parseFloat(String(temp.foodPricing)) < 0) {
      setErrorMsg('Food price cannot be negative');
      props.setOpen(true);
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `programMAXBudget` is forwarded from
       * the parent's own optional prop, so it is `undefined` wherever no program
       * budget was resolved. `n > undefined` is false for every `n`, so the
       * over-budget block is skipped and the order posts unchecked — the failure is
       * silent and permissive, in the direction of overspending.
       *
       * Note this guard is already unreachable for a second, independent reason
       * documented at `programOrderCost` above; both would have to be repaired for
       * the ceiling to be enforced.
       */
    } else if (!budgetOverrideChecked && tempProgramOrderCost > props.programMAXBudget!) {
      setErrorMsg(
        'Total order cost exceeds the program budget limit of ' +
          props.programMAXBudget +
          '. Please reduce the updated order cost.'
      );
    } else {
      setOrdersMessage('Updating order...');
      temp['lastUpdated'] = moment().utc().format();
      temp['lastUpdatedBy'] = authInfo.practitionerName;
      props.setWorking(true);
      props.setLoading(true);

      // REMOVE PII here from the order before it gets updated
      PATIENT_ATTRIBUTES.forEach((attribute) => {
        delete (temp as OrderWriteView)[attribute];
      });

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      FoodUpdater(`/order/` + selectedOrderId, authInfo.token, temp).then((response) => {
        props.setWorking(false);
        props.setLoading(false);
        if (response.statusCode === '200') {
          setOrdersMessage('Order Updated Successfully!');
          props.ordersMutate();
        } else {
          setOrdersMessage('Updating Error - ' + response.message);
        }
      });
      orderDetails = {};
      props.setOpen(false);
    }
  }

  return (
    <Modal
      as={Form}
      onOpen={() => props.setOpen(true)}
      onClose={() => props.setOpen(false)}
      open={props.open!}
      trigger={<Button icon="edit" content="Update" color="twitter" />}
      size="large"
    >
      <Modal.Header>Edit Order</Modal.Header>
      <Modal.Content scrolling>
        {errorMsg && <Message header="Oops!. Something went wrong.." content={errorMsg} negative />}
        <Form.Group widths="equal">
          {props.selectedOrder.bundleId && (
            <Form.Input label="Bundle ID" readOnly value={props.selectedOrder.bundleId} />
          )}
          {props.selectedOrder.bundleName && (
            <Form.Input label="Bundle Name" readOnly value={props.selectedOrder.bundleName} />
          )}
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Input label="Created By" readOnly value={props.selectedOrder.createdBy} />
          <Form.Input label="Last Updated By" readOnly value={props.selectedOrder.lastUpdatedBy} />
          {/*
            SUSPECTED DEFECT — REPORTED, NOT FIXED. The test asks `total`, not
            whether any entry came back. `Bundle.total` is optional in FHIR R4 and
            this server omits it on a search with no matches, which gives the
            answer the author wanted for "no referrals".

            It diverges if the server ever returns the bundle without `total` while
            entries exist (a legal R4 response): the selector disappears and the
            care manager cannot attach the order to a referral.
          */}
          {(referralData?.total ?? 0) > 0 && (
            <Form.Field
              autoComplete="off"
              label="Referral Id"
              options={referralIdList?.map((x) => {
                return { key: x, text: x.toUpperCase(), value: x };
              })}
              defaultValue={props?.selectedOrder?.referralId}
              control={Select}
              selectOnBlur={false}
              name="referralId"
              onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'referralId')}
            />
          )}
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field
            autoComplete="off"
            label="Status"
            options={statusList.map((x) => {
              return { key: x, text: x.toUpperCase(), value: x };
            })}
            defaultValue={props.selectedOrder.status}
            control={Select}
            selectOnBlur={false}
            name="status"
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'status')}
          />
          <SemanticDatepicker
            required
            autoComplete="false"
            onChange={(e, value) => handleChange(value as unknown as Parameters<typeof handleChange>[0], 'estimatedDeliveryDate')}
            datePickerOnly
            name="estimatedDeliveryDate"
            value={
              props.selectedOrder.estimatedDeliveryDate && props.selectedOrder.estimatedDeliveryDate !== 'Invalid date'
                ? moment(props.selectedOrder.estimatedDeliveryDate).local().toDate()
                : null
            }
            clearable={false}
            format="MM/DD/YYYY"
            id="form-input-control-food"
            label="Estimated Delivery Date"
            className="dobCalendar"
          />
          <Form.Field
            autoComplete="off"
            label="Delivery Date"
            name="deliveryDate"
            control={SemanticDatepicker}
            value={props.selectedOrder.deliveryDate ? moment(props.selectedOrder.deliveryDate).local().toDate() : null}
            onChange={handleDateChange}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Input
            autoComplete="off"
            label="Carrier"
            type="text"
            name="carrier"
            defaultValue={props.selectedOrder.deliveryInfo && props.selectedOrder.deliveryInfo.carrier}
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'carrier')}
          />
          <Form.Input
            autoComplete="off"
            label="Tracking Code"
            type="text"
            name="trackingCode"
            defaultValue={props.selectedOrder.deliveryInfo && props.selectedOrder.deliveryInfo.trackingCode}
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'trackingCode')}
          />
          {props.selectedOrder.vendorId === 'PK' && (
            <Form.Field
              autoComplete="off"
              label="Triage Order"
              options={triageOrderList.map((x) => {
                return { key: x, text: x, value: x };
              })}
              defaultValue={props.selectedOrder?.triageOrder}
              control={Select}
              selectOnBlur={false}
              name="triageOrder"
              onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'triageOrder')}
            />
          )}
          <Form.Field
            autoComplete="off"
            label="Triage Reason"
            options={triageReasonList.map((x) => {
              return { key: x, text: x.toUpperCase(), value: x };
            })}
            defaultValue={props.selectedOrder?.triageReason}
            control={Select}
            selectOnBlur={false}
            name="triageReason"
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'triageReason')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Input
            autoComplete="off"
            label="Food Price"
            type="number"
            min="1"
            name="foodPricing"
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'foodPricing')}
            onKeyDown={handleKeyDown}
            defaultValue={props.selectedOrder.foodPricing ? props.selectedOrder.foodPricing : ''}
          />
          <Form.Input
            autoComplete="off"
            label="Quantity"
            type="number"
            required
            max={15} // Limit to 15 for bundle orders
            name="bundleQuantity"
            defaultValue={props.selectedOrder.bundleQuantity ? props.selectedOrder.bundleQuantity : ''}
            onChange={(e, prop) => handleChange(e, 'bundleQuantity', prop.value)}
          />
          <Form.Input
            autoComplete="off"
            label="Shipping Fee"
            type="text"
            name="shippingFees"
            onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'shippingFees')}
            defaultValue={props.selectedOrder.shippingFees ? props.selectedOrder.shippingFees : ''}
          />
          {PROGRAMS_MEQ.includes(props.org!) && (
            <Form.Input
              autoComplete="off"
              label="Meal Equivalents"
              type="text"
              name="mealEquivalents"
              onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'mealEquivalents')}
              defaultValue={props.selectedOrder.mealEquivalents ? props.selectedOrder.mealEquivalents : ''}
            />
          )}
          <Form.Checkbox
            autoComplete="off"
            label="Budget Override"
            type="checkbox"
            name="budgetOverrideChecked"
            // onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) =>
            // handleChange(e, 'budgetOverrideChecked')}
            // onChange={(e) => handleChange(e, 'budgetOverrideChecked')}
            defaultValue={false}
            style={{
              backgroundColor: budgetOverrideChecked ? '#e6f3ff' : 'transparent',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: budgetOverrideChecked ? '2px solid #2185d0' : '2px solid transparent',
              color: '#2185d0',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={(event, data) => handleItemCheck(event, data, 'budgetOverrideChecked')}
          />
        </Form.Group>
        <Divider />
        <Comment.Group>
          <Header>Notes</Header>
          {noteList &&
            noteList.map((note, index) => {
              return (
                <Comment key={index}>
                  <Comment.Avatar src={'../../../../images/' + Helpers.getImage(true)} />
                  <Comment.Content>
                    <Comment.Author as="a">{note.author}</Comment.Author>
                    <Form.Group>
                      <Comment.Metadata>
                        <div>{moment.utc(note.time).fromNow()}</div>
                        <Form.Button
                          compact
                          size="mini"
                          color="red"
                          content="Remove"
                          onClick={() => removeNote(index)}
                        />
                        <Form.Button
                          compact
                          size="mini"
                          color="orange"
                          content="Edit"
                          onClick={() => {
                            setEditNotePageOpen(true);
                            setUpdatedNote({ noteText: note.text });
                            setSelectedNoteIndex(index);
                          }}
                        />
                        {selectedNoteIndex === index && (
                          <Modal onClose={() => setEditNotePageOpen(false)} open={editNotePageOpen} size="small">
                            <Modal.Content>
                              <Form.Field>
                                <Form.Input
                                  width={16}
                                  rows={3}
                                  control={TextArea}
                                  name="noteText"
                                  value={updatedNote.noteText}
                                  placeholder="Edit Note"
                                  onChange={handleNoteChange}
                                />
                              </Form.Field>
                            </Modal.Content>
                            <Modal.Actions>
                              <Button
                                size="medium"
                                color="blue"
                                content="Submit"
                                style={{ marginLeft: '10px' }}
                                onClick={editNote}
                              />
                            </Modal.Actions>
                          </Modal>
                        )}
                      </Comment.Metadata>
                    </Form.Group>
                    <Comment.Text>{note.text}</Comment.Text>
                  </Comment.Content>
                </Comment>
              );
            })}
        </Comment.Group>
        <Form.Group>
          <Form.Input
            width={16}
            rows={3}
            control={TextArea}
            name="noteText"
            value={noteText}
            cols={3}
            placeholder="Add Note"
            onChange={addNoteField}
          />
          <div>
            <Form.Button size="medium" color="blue" content="Add" style={{ marginLeft: '10px' }} onClick={addNote} />
          </div>
        </Form.Group>
      </Modal.Content>
      <Modal.Actions>
        <Button
          content="Update"
          type="submit"
          positive
          onClick={(e) => {
            updateOrderFunc(e);
          }}
        />
        <Button content="Close" onClick={() => props.setOpen(false)} negative />
      </Modal.Actions>
    </Modal>
  );
}

export default UpdateOrder;
